// static/js/components/ui/ToolResultDisplay.jsx

/**
 * ToolResultDisplay.jsx
 *
 * This JSX file defines the `ToolResultDisplay` component which serves as a dynamic display for various types of tool results within the application. 
 * It is designed to handle and present different data types including images, CSV datasets, JSON metadata, and markdown content.
 * The component integrates functionalities such as downloading and attaching datasets directly from the UI. 
 * It provides interactive UI elements like buttons for attaching datasets to other components or downloading them, 
 * and it utilizes conditional rendering to only display relevant sections based on the available data.
 *
 * The component expects a `toolResult` object containing paths and data for different result types (image, csv, json, markdown),
 * and utilizes callback functions like `onAttach` to manage user interactions with the displayed results.
 * Additionally, it incorporates custom components like `Image`, `StructuredData`, `MarkdownData`, and `AttachmentButton` to effectively present the data.
 */

import React, { useMemo } from 'react';
import Image from './ImageResult';
import StructuredData from './StructuredDataResult';
import MarkdownData from './MarkdownDataResult';
import AttachmentButton from './AttachmentButton';
import { Download } from 'lucide-react';
import DOMPurify from 'dompurify'; // for content sanitization

// Configure DOMPurify to enhance security
DOMPurify.addHook('afterSanitizeAttributes', function(node) {
  // Set all elements with a target attribute to target=_blank and add security attributes
  if ('target' in node) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
  
  // Add security attributes to all links
  if (!node.hasAttribute('target') && 
      (node.hasAttribute('xlink:href') || node.hasAttribute('href'))) {
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

/**
 * Component for displaying and managing tool outputs with attachment capabilities.
 * 
 * @param {Object} props Component props
 * @param {Object} props.toolResult Tool result containing dataset and file info
 * @param {Function} props.onAttach Callback when datasets are attached
 * @param {Object} props.attachedTypes Currently attached dataset types
 */
const ToolResultDisplay = ({ 
  toolResult, 
  onAttach,
  attachedTypes = {} 
}) => {
  if (!toolResult) return null;

  // Helper to check if path is valid
  const isValidPath = (path) => {
    return path && path !== 'None' && !path.includes('undefined');
  };

  // Determine available dataset types for this tool result
  const availableTypes = useMemo(() => ({
    image: Boolean(toolResult.image_filepath && toolResult.image_filepath !== 'None'),
    csv: Boolean(toolResult.csv_dataset && toolResult.csv_filepath !== 'None'),
    json: Boolean(toolResult.json_dataset && toolResult.json_filepath !== 'None'),
    markdown: Boolean(toolResult.md_dataset && toolResult.md_filepath !== 'None')
  }), [toolResult]);

  // Handle file downloads
  const handleDownload = (filepath, filename) => {
    const sanitizedFilename = DOMPurify.sanitize(filename); // Sanitize filename
    window.open(`/download_tool_result/${sanitizedFilename}`, '_blank');
  };

  // Helper function to sanitize alt messages and other text content
  const sanitizeText = (text) => {
    if (!text) return '';
    return DOMPurify.sanitize(text);
  };

  return (
    <div className="tool-result-container">
      {/* Display alt message if present */}
      {toolResult.alt_msg && (
        <div className="text-sm text-primary-text/60 mb-4">
          {sanitizeText(toolResult.alt_msg)} {/* Sanitizes alt_msg */}
        </div>
      )}

      {/* Divider between multiple results */}
      <div className="border-b border-primary-border/20 my-4"></div>

      {/* Image result section */}
      {toolResult.image_filepath && toolResult.image_filepath !== 'None' && (
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-primary-text font-medium">Generated Image</h4>
            <div className="flex items-center gap-2">
              <AttachmentButton
                datasetTypes={{ image: availableTypes.image }}
                onAttach={onAttach}
                isAttached={attachedTypes.image}
              />
              <button
                onClick={() => handleDownload(
                  toolResult.image_filepath, 
                  toolResult.image_filepath.split('/').pop()
                )}
                className="p-2 text-primary-text hover:bg-primary-700/20 rounded-lg transition-colors"
                title="Download image"
              >
                <Download size={20} />
              </button>
            </div>
          </div>
          <Image
            src={toolResult.image_filepath}
            alt={sanitizeText(toolResult.alt_msg) || 'Tool result'}
            showHeader={false} // prevents duplicate "Generated Image" and download button when that is already provided by ImageResult.jsx
          />
        </div>
      )}

      {/* CSV dataset section */}
      {(toolResult.csv_dataset && toolResult.csv_filepath) && (
        <div className="relative mt-6 border rounded-lg p-4 bg-primary-700/10">
          <div className="flex justify-between items-center">
            <h4 className="text-primary-text font-medium">Dataset</h4>
            <div className="flex items-center gap-2">
              <AttachmentButton
                datasetTypes={{ csv: availableTypes.csv }}
                onAttach={onAttach}
                isAttached={attachedTypes.csv}
              />
              <button
                onClick={() => handleDownload(
                  toolResult.csv_filepath, 
                  toolResult.csv_filepath.split('/').pop()
                )}
                className="p-2 text-primary-text hover:bg-primary-700/20 rounded-lg transition-colors"
                title="Download CSV"
              >
                <Download size={20} />
                {/* Disabled: would display text inside CSV download box */}
                {/* <span className="whitespace-nowrap"></span> */}
              </button>
            </div>
          </div>

          {/* Displays CSV preview when attached */}
          {attachedTypes.csv && (
            <div className="mt-4">
              {/* Sanitizes CSV data by passing through sanitizeText if it's a string */}
              <StructuredData 
                data={typeof toolResult.csv_dataset === 'string' 
                  ? sanitizeText(toolResult.csv_dataset) 
                  : toolResult.csv_dataset} 
                type="csv" 
              />
            </div>
          )}

        </div>
      )}

      {/* Markdown content section */}
      {toolResult.md_dataset && (
        <div className="relative mt-6 border rounded-lg p-4 bg-primary-700/10">
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-primary-text font-medium">Fetched Content</h4>
            <div className="flex items-center gap-2">
              <AttachmentButton
                datasetTypes={{ markdown: availableTypes.markdown }}
                onAttach={onAttach}
                isAttached={attachedTypes.markdown}
              />
              {toolResult.md_filepath && toolResult.md_filepath !== 'None' && (
                <button
                  onClick={() => handleDownload(
                    toolResult.md_filepath, 
                    toolResult.md_filepath.split('/').pop()
                  )}
                  className="p-2 text-primary-text hover:bg-primary-700/20 rounded-lg transition-colors"
                  title="Download Markdown"
                >
                  <Download size={20} />
                </button>
              )}
            </div>
          </div>
          <MarkdownData data={sanitizeText(toolResult.md_dataset)} /> {/* Sanitizes markdown data */}
        </div>
      )}

      {/* JSON data section */}
      {toolResult.json_dataset && (
        <div className="relative mt-6 border rounded-lg p-4 bg-primary-700/10">
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-primary-text font-medium">Fetched Metadata</h4>
            <div className="flex items-center gap-2">
              <AttachmentButton
                datasetTypes={{ json: availableTypes.json }}
                onAttach={onAttach}
                isAttached={attachedTypes.json}
              />
              {toolResult.json_filepath && toolResult.json_filepath !== 'None' && (
                <button
                  onClick={() => handleDownload(
                    toolResult.json_filepath, 
                    toolResult.json_filepath.split('/').pop()
                  )}
                  className="p-2 text-primary-text hover:bg-primary-700/20 rounded-lg transition-colors"
                  title="Download JSON"
                >
                  <Download size={20} />
                </button>
              )}
            </div>
          </div>
          <StructuredData 
            data={typeof toolResult.json_dataset === 'string' ? 
              sanitizeText(toolResult.json_dataset) :  // Sanitize JSON string data
              toolResult.json_dataset  // Object data will be sanitized inside StructuredData component
            } 
            type="json" 
          />
        </div>
      )}
    </div>
  );
};

export default ToolResultDisplay;